import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import de from 'date-fns/locale/de';
import { useNavigate } from 'react-router-dom';
import theme from '../theme';
import config from '../config';
import { useAuth } from '../AuthContext';

// Keyframes for sparkles
const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3); }
  50% { box-shadow: 0 6px 30px rgba(219, 39, 98, 0.6), 0 0 30px rgba(241, 222, 124, 0.5); }
`;

registerLocale('de', de);

const BookingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-weight: 600;
`;

const DatePickerContainer = styled.div`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid ${props => props.theme.colors.accent1};
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: ${props => props.theme.colors.secondary};
    color: white;
    border-bottom: none;
    border-radius: ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0 0;
  }

  .react-datepicker__current-month {
    color: white;
    font-weight: 600;
  }

  .react-datepicker__day-name {
    color: white;
  }

  .react-datepicker__day {
    border-radius: 50%;
    margin: 2px;
    transition: all 0.2s ease;
  }

  .react-datepicker__day:hover {
    background-color: ${props => props.theme.colors.accent1} !important;
    color: ${props => props.theme.colors.primary} !important;
  }

  .react-datepicker__day--selected {
    background-color: ${props => props.theme.colors.secondary} !important;
    color: white !important;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: ${props => props.theme.colors.accent1} !important;
    color: ${props => props.theme.colors.primary} !important;
  }

  .react-datepicker__day--disabled {
    color: #ccc !important;
  }
`;

const DatePickerButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: ${props => props.selectedDate ? props.theme.colors.primary : '#666'};
  font-weight: ${props => props.selectedDate ? '600' : '400'};

  &:hover {
    background-color: ${props => props.theme.colors.accent1};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
`;

const DatePickerWrapper = styled.div`
  position: relative;
`;

const CustomDatePicker = styled(DatePicker)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
`;

const SlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 10px;
`;

const SlotButton = styled.button`
  padding: 12px 16px;
  border: 2px solid ${props => props.available ? props.theme.colors.accent1 : '#e0e0e0'};
  border-radius: ${props => props.theme.borderRadius.medium};
  background-color: ${props => props.selected ? props.theme.colors.secondary : 'white'};
  color: ${props => {
    if (props.selected) return 'white';
    if (props.available) return props.theme.colors.primary;
    return '#999';
  }};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.available ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  opacity: ${props => props.available ? 1 : 0.6};

  &:hover {
    ${props => props.available && `
      background-color: ${props.theme.colors.accent1};
      color: ${props.theme.colors.primary};
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `}
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingText = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
`;

const NoSlotsText = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-style: italic;
`;

const SparkleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
  top: 0;
  left: 0;
`;

const Sparkle = styled.span`
  position: absolute;
  font-size: 10px;
  color: ${props => props.theme.colors.accent1};
  opacity: 0;
  animation: ${sparkle} 2s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 20%;
    left: 15%;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 20%;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    bottom: 25%;
    left: 25%;
    animation-delay: 0.5s;
  }
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  display: block;
  padding: 16px 40px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 50%, ${props => props.theme.colors.secondary} 100%);
  background-size: 200% 200%;
  color: white;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  margin-bottom: -10px;
  box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
  animation: ${glow} 3s ease-in-out infinite;

  &::before {
    content: '✨';
    position: absolute;
    font-size: 14px;
    top: -8px;
    left: 10px;
    animation: ${sparkle} 2s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '⭐';
    position: absolute;
    font-size: 12px;
    bottom: -6px;
    right: 15px;
    animation: ${sparkle} 2.5s ease-in-out infinite 0.5s;
    pointer-events: none;
  }

  &:hover {
    background-position: 100% 0;
    box-shadow: 0 8px 35px rgba(219, 39, 98, 0.5), 0 0 24px rgba(241, 222, 124, 0.4);
    transform: scale(1.01);
    animation: none;
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    animation: none;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3);
    }
  }
`;

const TrialLessonBooking = ({ onDatePickerFocus, onDatePickerBlur, onClose }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectableDates, setSelectableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Fetch selectable dates on component mount
  useEffect(() => {
    const fetchSelectableDates = async () => {
      try {
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const viewportWidth = window.innerWidth;
        const response = await fetch(config.getApiUrl(`/api/trial-lessons/selectable-dates?viewportWidth=${viewportWidth}`), { headers });
        if (response.ok) {
          const data = await response.json();
          setSelectableDates(data.selectableDates);
        } else {
          console.error('Failed to fetch selectable dates');
          setSelectableDates([]);
        }
      } catch (error) {
        console.error('Error fetching selectable dates:', error);
        setSelectableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };

    fetchSelectableDates();
  }, [token]);

  const fetchAvailableSlots = async (date) => {
    if (!date) return;
    
    setLoading(true);
    try {
      // Add one day to compensate for timezone conversion
      const adjustedDate = new Date(date);
      adjustedDate.setDate(adjustedDate.getDate() + 1);
      const dateString = adjustedDate.toISOString().split('T')[0];
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const viewportWidth = window.innerWidth;
      const response = await fetch(config.getApiUrl(`/api/trial-lessons/slots/${dateString}?viewportWidth=${viewportWidth}`), { headers });
      
      if (response.ok) {
        const slots = await response.json();
        setAvailableSlots(slots);
      } else {
        console.error('Failed to fetch slots');
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setIsDatePickerOpen(false);
    fetchAvailableSlots(date);
  };

  const handleButtonClick = () => {
    if (!loadingDates) {
      setIsDatePickerOpen(!isDatePickerOpen);
      if (!isDatePickerOpen) {
        onDatePickerFocus?.();
      } else {
        onDatePickerBlur?.();
      }
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter function that uses the backend data
  const filterDate = (date) => {
    if (loadingDates) return false; // Don't allow selection while loading
    
    // Add one day to compensate for timezone conversion (same logic as fetchAvailableSlots)
    const adjustedDate = new Date(date);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    const dateString = adjustedDate.toISOString().split('T')[0];
    
    return selectableDates.includes(dateString);
  };

  const handleNavigateToBilling = () => {
    if (!selectedDate || !selectedSlot) {
      return;
    }

    // Add one day to compensate for timezone conversion
    const adjustedDate = new Date(selectedDate);
    adjustedDate.setDate(adjustedDate.getDate() + 1);
    const dateString = adjustedDate.toISOString().split('T')[0];
    
    // Navigate to trial lesson purchase page with selected slot info
    navigate('/trial-lesson-purchase', {
      state: {
        date: dateString,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        formattedDate: formatDate(selectedDate)
      }
    });
  };

  return (
    <BookingContainer>
      <Section>
        <SectionTitle>Datum auswählen</SectionTitle>
        <DatePickerContainer>
          <DatePickerWrapper>
            <DatePickerButton
              onClick={handleButtonClick}
              disabled={loadingDates}
              selectedDate={selectedDate}
            >
              {loadingDates 
                ? "Lade verfügbare Termine..." 
                : selectedDate 
                  ? selectedDate.toLocaleDateString('de-DE', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    })
                  : "Datum auswählen..."
              }
            </DatePickerButton>
            {isDatePickerOpen && (
              <CustomDatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                onBlur={() => {
                  setIsDatePickerOpen(false);
                  onDatePickerBlur?.();
                }}
                minDate={new Date()}
                filterDate={filterDate}
                dateFormat="dd.MM.yyyy"
                locale="de"
                inline
                open={isDatePickerOpen}
              />
            )}
          </DatePickerWrapper>
        </DatePickerContainer>
      </Section>

      {selectedDate && (
        <Section>
          <SectionTitle>
            Verfügbare Zeiten für {formatDate(selectedDate)}
          </SectionTitle>
          {loading ? (
            <LoadingText>Lade verfügbare Zeiten...</LoadingText>
          ) : availableSlots.length > 0 ? (
            <SlotsGrid>
              {availableSlots.map((slot, index) => (
                <SlotButton
                  key={index}
                  available={slot.available}
                  selected={selectedSlot === slot}
                  onClick={() => handleSlotSelect(slot)}
                >
                  {slot.startTime} - {slot.endTime}
                </SlotButton>
              ))}
            </SlotsGrid>
          ) : (
            <NoSlotsText>Keine verfügbaren Zeiten für diesen Tag.</NoSlotsText>
          )}
        </Section>
      )}

      {selectedSlot && (
        <Section>
          <SectionTitle>Ausgewählte Zeit</SectionTitle>
          <p style={{ 
            fontSize: '18px', 
            color: '#222',
            fontWeight: 500,
            textAlign: 'center',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: theme.borderRadius.medium,
            margin: 0,
            border: `1px solid #e0e0e0`
          }}>
            {formatDate(selectedDate)} um {selectedSlot.startTime} - {selectedSlot.endTime}
          </p>
          <ActionButton
            onClick={handleNavigateToBilling}
          >
            <SparkleContainer>
              <Sparkle>✨</Sparkle>
              <Sparkle>⭐</Sparkle>
              <Sparkle>✨</Sparkle>
            </SparkleContainer>
            Buchung finalisieren
          </ActionButton>
        </Section>
      )}
    </BookingContainer>
  );
};

export default TrialLessonBooking; 